import React, { useState } from 'react';
import { PersonaProfileResponse, UpdatePersonaProfileBody } from '../../api/types';
import { PlusIcon, PencilIcon, TrashIcon, ExclamationCircleIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface ProfilesSectionProps {
  profiles: PersonaProfileResponse[];
  onAddProfile: () => void;
  onDeleteProfile: (profileId: string) => void;
  /**
   * Updated to accept the new profile name.
   * The parent can then update the actual profile data.
   */
  onEditProfile: (profileId: string, body: UpdatePersonaProfileBody) => void;
}

const getProfileName = (profile: PersonaProfileResponse): string => {
  if (profile.displayName) {
    return profile.displayName;
  }
  return profile.type === 'main' ? 'Main' : 'Secondary';
}

/**
 * Always shows "My Profiles" plus a grid of profile cards.
 * Each card shows:
 *   - The face if assigned (or "No face assigned")
 *   - Inline edit mode for the display name (pencil icon => text input)
 *   - A delete icon to remove the profile
 * At the end, a dashed "Add New Profile" card for easy creation.
 */
export const ProfilesSection: React.FC<ProfilesSectionProps> = ({
    profiles,
    onAddProfile,
    onDeleteProfile,
    onEditProfile,
  }) => {
  // Local state to manage inline editing
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');

  /**
   * When user clicks the pencil icon, switch to edit mode for this specific profile.
   */
  const startEditing = (profileId: string, currentName: string) => {
    setEditingProfileId(profileId);
    setEditName(currentName || '');
    setError('');
  };

  /**
   * Cancel editing and revert any changes.
   */
  const cancelEditing = () => {
    setEditingProfileId(null);
    setEditName('');
    setError('');
  };

  /**
   * Validate and save the new name, then call parent callback.
   */
  const saveName = (profileId: string) => {
    if (editName.trim().length < 2) {
      setError('Profile name must be at least 2 characters long');
      return;
    }
    onEditProfile(profileId, { displayName: editName.trim() });
    // Reset local state
    setEditingProfileId(null);
    setEditName('');
    setError('');
  };

  return (
    <div className="mb-8">
      {/* Heading */}
      <div className='mb-4'>
        <h2 className='text-lg font-medium text-slate-800'>My Profiles</h2>
      </div>

      {/* Grid of profiles + "Add new" card */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {profiles.map((profile) => {
          const isEditing = editingProfileId === profile.id;
          return (
            <div key={profile.id} className="bg-white shadow rounded-lg overflow-hidden">
              {/* Image area (face if assigned) */}
              <div className="relative aspect-square overflow-hidden">
                {profile.url ? (
                  <img
                    src={profile.url}
                    alt={profile.displayName || ''}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className='h-full w-full bg-gray-200 flex flex-col items-center justify-center p-3 text-center'>
                    <span className='text-sm text-gray-600 mb-1'>
                      No face assigned
                    </span>
                    <p className='text-xs text-gray-500'>
                      <Link
                        to='/create-neoface'
                        className='text-indigo-600 hover:underline'
                      >
                        Create a new Neoface
                      </Link>{' '}
                      or select from below
                    </p>
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className='p-4 flex items-center justify-between'>
                {/* Display name or inline edit input */}
                {isEditing ? (
                  <div className='relative'>
                    {/* The input with possible error styling */}
                    <div className={`relative rounded-md shadow-sm ${error ? 'pr-10' : ''}`}>
                      <input
                        type='text'
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                        className={`
                          block w-40 rounded-md border-0 py-1.5 pr-8
                          ${
                          error
                            ? 'text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-indigo-600'
                        }
                          focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6
                        `}
                        placeholder="Profile Name"
                      />
                      {error && (
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ExclamationCircleIcon className="h-4 w-4 text-red-500" />
                        </div>
                      )}
                    </div>

                    {error && (
                      <p className="mt-1 text-xs text-red-600">{error}</p>
                    )}

                    <div className="mt-1 flex space-x-2">
                      <button
                        onClick={() => saveName(profile.id)}
                        className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-1
                                   text-xs font-medium text-white hover:bg-indigo-500"
                      >
                        <CheckIcon className="h-4 w-4" />
                        Save
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1
                                   text-xs font-medium text-gray-700 hover:bg-gray-200"
                      >
                        <XMarkIcon className="h-4 w-4" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Static name + pencil icon
                  <div className="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <span>
                      {getProfileName(profile)}
                    </span>
                    <button
                      onClick={() => startEditing(profile.id, getProfileName(profile))}
                      className="text-gray-400 hover:text-gray-600"
                      title="Edit Profile Name"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Delete icon */}
                <button
                  onClick={() => onDeleteProfile(profile.id)}
                  className={`inline-flex items-center justify-center px-2 py-2
                             ${profile.type === 'primary' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-red-50 text-red-600 hover:text-red-700 hover:bg-red-100'}
                             rounded-lg transition-colors`}
                  title={profile.type === 'primary' ? 'Cannot delete main profile' : 'Delete Profile'}
                  disabled={profile.type === 'primary'}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          );
        })}

        {/* "Add New Profile" Card with dashed border */}
        {profiles.length < 4 && <div
          className="group flex flex-col items-center justify-center
                     border-2 border-dashed border-gray-300 rounded-lg
                     bg-white text-gray-500 hover:bg-gray-50 transition-colors"
        >
          <button
            onClick={onAddProfile}
            className="flex flex-col items-center p-6 text-center"
          >
            <div
              className="flex items-center justify-center w-12 h-12 mb-2
                         rounded-full bg-gray-100 group-hover:bg-gray-200 transition"
            >
              <PlusIcon className="h-6 w-6 text-gray-400 group-hover:text-gray-500" />
            </div>
            <span className="text-sm font-medium">Create New Profile</span>
          </button>
        </div> }
      </div>
    </div>
  );
};
